import React, { Component } from "react";
import { connect } from "react-redux";
import { Form, Input, Button, Row, Col, Card, Typography, Divider, Alert, Space } from "antd";
import { SendOutlined, ArrowLeftOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import queryString from "query-string";
import { resetPassword } from "../../redux/actions/salon-account/authenticate";
import { NewMessageError } from "../../component/common/MessageError/NewMessageError";
import "./resetPassword.css";

const { Title, Text } = Typography;

const layout = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
};

const PASSWORD_REGEX = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/;

class ResetPassword extends Component {
    state = { token: null };

    componentDidMount() {
        const queryObject = queryString.parse(this.props.history.location.search);
        const token = queryObject.token;
        if (token) this.setState({ token });
    }

    componentDidUpdate(prevProps) {
        const { error, message } = this.props;

        // only fire toast when changed
        if (message !== prevProps.message || error !== prevProps.error) {
            NewMessageError(message, error, null);
        }

        if (message && message !== prevProps.message) {
            window.location.href = "https://www.partnersdash.com";
        }
    }

    onFinish = (values) => {
        const { newPassword } = values;
        const { token } = this.state;
        this.props.resetPassword(token, newPassword);
    };

    render() {
        const { loading, error } = this.props;
        const year = new Date().getFullYear();

        return (
            <div className="dash-reset-page">
                <Row justify="center" align="middle" className="dash-reset-row">
                    <Col xs={22} sm={20} md={14} lg={10} xl={8} xxl={7}>
                        <Card className="dash-reset-card" bordered={false}>
                            {/* HTML logo (no image) */}
                            <div className="dash-reset-header">
                                <div className="dash-logo">
                                    Dash Booking
                                </div>

                                <Title level={3} style={{ marginTop: 10, marginBottom: 0 }}>
                                    Reset Password
                                </Title>
                                <Text type="secondary">Create a strong password to protect your account.</Text>
                            </div>

                            <Divider />

                            {error && (
                                <Alert
                                    message="Reset failed"
                                    description={error}
                                    type="error"
                                    showIcon
                                    style={{ marginBottom: 16 }}
                                />
                            )}

                            <Form
                                {...layout}
                                layout="vertical"
                                onFinish={this.onFinish}
                                size="large"
                                className="dash-reset-form"
                            >
                                <Form.Item
                                    label="New Password"
                                    name="newPassword"
                                    hasFeedback
                                    rules={[
                                        { required: true, message: "Please enter your new password!" },
                                        {
                                            validator: async (_, npw) => {
                                                if (!npw) return Promise.resolve();
                                                if (!PASSWORD_REGEX.test(npw)) {
                                                    return Promise.reject(
                                                        new Error("Must be 8+ chars with uppercase, lowercase, and a number.")
                                                    );
                                                }
                                                return Promise.resolve();
                                            },
                                        },
                                    ]}
                                >
                                    <Input.Password placeholder="New password" autoComplete="new-password" />
                                </Form.Item>

                                <Form.Item
                                    label="Verify Password"
                                    name="verifyPassword"
                                    dependencies={["newPassword"]}
                                    hasFeedback
                                    rules={[
                                        { required: true, message: "Please confirm your password!" },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value || getFieldValue("newPassword") === value) return Promise.resolve();
                                                return Promise.reject(new Error("Passwords do not match."));
                                            },
                                        }),
                                    ]}
                                >
                                    <Input.Password placeholder="Re-type new password" autoComplete="new-password" />
                                </Form.Item>

                                <div className="dash-requirements">
                                    <div className="dash-requirements-title">
                                        <SafetyCertificateOutlined />
                                        <span>Password requirements</span>
                                    </div>
                                    <ul>
                                        <li>At least 1 lowercase letter</li>
                                        <li>At least 1 uppercase letter</li>
                                        <li>At least 1 number</li>
                                        <li>Minimum 8 characters</li>
                                    </ul>
                                </div>

                                <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                    <Button
                                        type="primary"
                                        icon={<SendOutlined />}
                                        htmlType="submit"
                                        loading={loading}
                                        className="dash-reset-btn"
                                        block
                                    >
                                        Reset Password
                                    </Button>

                                    <Button href="/" style={{ color: "#0097b2" }} type="link" className="dash-reset-back">
                                        <ArrowLeftOutlined /> Back to Login
                                    </Button>
                                </Space>
                            </Form>

                            <Divider />

                            <div className="dash-reset-footer">
                                <Text type="secondary">{year} © Dash Booking Inc.</Text>
                                <div className="dash-reset-links">
                                    <a
                                        href="https://www.dashbooking.com/business/partners-terms-and-conditions"
                                        rel="noreferrer"
                                        target="_blank"
                                    >
                                        Terms
                                    </a>
                                    <a
                                        href="https://www.dashbooking.com/privacy-policy"
                                        rel="noreferrer"
                                        target="_blank"
                                    >
                                        Privacy
                                    </a>
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    loading: state.authenticate.loading,
    error: state.authenticate.error,
    message: state.authenticate.message,
});

const mapDispatchToProps = (dispatch) => ({
    resetPassword: (token, password) => dispatch(resetPassword(token, password)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ResetPassword);